import { RubyGemMark } from '../components/RubyGemMark';
/**
 * UNIFIED SUPPORT CENTER — merges the old /support (ext contact-form tickets)
 * and /notifications (tenant HQ threads) pages into one queue backed by
 * support-service's `/admin/issues*` endpoints. A single AppIssue can now be
 * an app bug, a device problem, a billing question, or a guided "question" —
 * see packages/shared-types src/schemas/entities/app-issue.schema.ts.
 *
 * Legacy super-admin-ext contact-form tickets (no tenant account) keep their
 * own fetch path and render as a "Guests & prospects (LEGACY)" group so
 * nothing already deployed breaks.
 *
 * /app-issues (in-app bug reports queue) is untouched — its tickets simply
 * also show up here since they live in the same support-service table.
 */
import { RubyLoader } from '../components/RubyLoader';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Search, RefreshCw, Send, X, Copy, Check, Phone, Headphones,
  ChevronDown, AlertCircle, Trash2,
} from 'lucide-react';
import { authedRequest, api } from '../services/apiService';
import {
  issuesApi, AdminIssue, AdminIssueComment, IssueAssignmentPatch, buildAssignmentPatch,
} from '../services/issuesApi';
import { toastService } from '../services/toastService';
import type { Tenant } from '../types';

const RUBY = '#DC143C';

// ---------------------------------------------------------------------------
// Legacy super-admin-ext contact-form tickets ("Guests & prospects") — no
// tenant account, so they can't live in support-service's tenant-scoped
// table. Base-URL resolution + shapes are carried over unchanged from the
// page this file replaces; every call is now wrapped in try/catch + toast
// (the old page silently swallowed failures).
// ---------------------------------------------------------------------------
const getSuperAdminExtApiBase = (): string => {
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return '/support-api';
  }
  const isDev = hostname.includes('dev.') || hostname.includes('staging.') || hostname.includes('stage.');
  if (hostname.includes('rubytill')) {
    return isDev ? 'https://support.api.dev.rubytill.com' : 'https://support.api.rubytill.com';
  }
  if (hostname.includes('terrakron')) {
    return isDev ? 'https://support.api.dev.terrakron.com' : 'https://support.api.terrakron.com';
  }
  return isDev ? 'https://support.api.dev.coffeemasterpro.com' : 'https://support.api.coffeemasterpro.com';
};
const EXT_API = getSuperAdminExtApiBase();

interface LegacyTicket {
  id: string;
  name: string;
  email: string;
  phone?: string;
  subject: string;
  message: string;
  category: string;
  priority: string;
  status: string;
  type?: string;
  businessName?: string;
  createdAt: number;
  updatedAt: number;
  replies?: Array<{ id: string; message: string; from: string; createdAt: number }>;
}

// ---------------------------------------------------------------------------
// Shared vocab across both sources
// ---------------------------------------------------------------------------
const KIND_EMOJI: Record<string, string> = {
  issue: '🐞', device: '🖥️', billing: '💳', question: '❓', contact: '✉️', legacy: '✉️',
};
const KIND_LABEL: Record<string, string> = {
  issue: 'App issue', device: 'Device problem', billing: 'Billing', question: 'Question',
  contact: 'Contact', legacy: 'Guest inquiry',
};
const effectiveKind = (iss: AdminIssue): string => iss.kind || 'issue';

const STATUS_META: Record<string, { label: string; cls: string }> = {
  new: { label: 'New', cls: 'bg-blue-100 text-blue-700' },
  open: { label: 'Open', cls: 'bg-blue-100 text-blue-700' },
  in_progress: { label: 'In Progress', cls: 'bg-amber-100 text-amber-700' },
  deploying: { label: 'Deploying', cls: 'bg-cyan-100 text-cyan-700' },
  awaiting_retest: { label: 'Ready to re-test', cls: 'bg-emerald-100 text-emerald-700' },
  waiting_response: { label: 'Waiting', cls: 'bg-stone-200 text-stone-600' },
  resolved: { label: 'Resolved', cls: 'bg-emerald-50 text-emerald-700' },
  rejected: { label: 'Rejected', cls: 'bg-red-100 text-red-700' },
  closed: { label: 'Closed', cls: 'bg-stone-200 text-stone-600' },
};
const isTerminalStatus = (s: string) => s === 'resolved' || s === 'rejected' || s === 'closed';
const isWaitingStatus = (s: string) => s === 'awaiting_retest' || s === 'waiting_response';
const isUrgentPriority = (p?: string) => p === 'blocking' || p === 'critical' || p === 'urgent';

const STATUS_OPTIONS = [
  { value: 'new', label: 'New' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'deploying', label: 'Deploying' },
  { value: 'awaiting_retest', label: 'Awaiting Retest' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'rejected', label: 'Rejected' },
];
const PRIORITY_OPTIONS = [
  { value: 'minor', label: 'Minor' },
  { value: 'annoying', label: 'Annoying' },
  { value: 'blocking', label: 'Blocking' },
  { value: 'critical', label: 'Critical' },
];
const LEGACY_STATUS_OPTIONS = [
  { value: 'open', label: 'Open' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'waiting_response', label: 'Waiting' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'closed', label: 'Closed' },
];
const STATUS_FILTER_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'all', label: 'All' },
  { value: 'new', label: 'New' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'deploying', label: 'Deploying' },
  { value: 'awaiting_retest', label: 'Awaiting Retest' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'rejected', label: 'Rejected' },
];
const TYPE_FILTER_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'issue', label: 'App issue' },
  { value: 'device', label: 'Device' },
  { value: 'billing', label: 'Billing' },
  { value: 'question', label: 'Question' },
  { value: 'legacy', label: 'Guests (legacy)' },
];

const timeAgo = (ts: number): string => {
  const s = Math.floor((Date.now() - ts) / 1000);
  if (s < 60) return 'now';
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
};
const fmtFullDate = (ts: number) => new Date(ts).toLocaleString('en-US', {
  month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
});
const initials = (name: string) => (name || '').split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]?.toUpperCase() || '').join('') || '—';

// ---------------------------------------------------------------------------
// Unified row — a single shape the list/grouping/search logic operates on,
// built from either a support-service AdminIssue or a legacy contact ticket.
// ---------------------------------------------------------------------------
interface UnifiedRow {
  id: string;
  legacy: boolean;
  kind: string;
  title: string;
  status: string;
  priority?: string;
  tenantKey: string;
  tenantName: string;
  createdAt: number;
  updatedAt: number;
  serial?: string;
}

const fromIssue = (iss: AdminIssue, tenantsById: Map<string, Tenant>): UnifiedRow => {
  // Guest/contact issues created without a real tenant account (tenantId
  // 'public', or missing) belong in the same "Guests & prospects" bucket as
  // the legacy contact-form tickets, not in a separate "Unknown tenant" group.
  const isGuest = !iss.tenantId || iss.tenantId === 'public';
  return {
    id: iss.id,
    legacy: false,
    kind: effectiveKind(iss),
    title: iss.title,
    status: iss.status,
    priority: iss.priority,
    tenantKey: isGuest ? 'legacy' : iss.tenantId,
    tenantName: isGuest
      ? 'Guests & prospects'
      : (tenantsById.get(iss.tenantId)?.name || iss.context?.tenantName || 'Unknown tenant'),
    createdAt: iss.createdAt,
    updatedAt: iss.updatedAt,
    serial: iss.device?.serialNumber,
  };
};

const fromLegacy = (t: LegacyTicket): UnifiedRow => ({
  id: t.id,
  legacy: true,
  kind: 'legacy',
  title: t.subject,
  status: t.status,
  priority: t.priority,
  tenantKey: 'legacy',
  tenantName: 'Guests & prospects',
  createdAt: t.createdAt,
  updatedAt: t.updatedAt,
});

// ---------------------------------------------------------------------------
// Small shared UI primitives
// ---------------------------------------------------------------------------
const StatusPill: React.FC<{ status: string }> = ({ status }) => {
  const meta = STATUS_META[status] || { label: status, cls: 'bg-stone-100 text-stone-600' };
  return <span className={`px-2 py-0.5 rounded-full text-[10.5px] font-bold whitespace-nowrap ${meta.cls}`}>{meta.label}</span>;
};

const Dropdown: React.FC<{
  label: string;
  value: string;
  options: Array<{ value: string; label: string }>;
  onChange: (v: string) => void;
  triggerClassName?: string;
}> = ({ label, value, options, onChange, triggerClassName }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);
  const current = options.find((o) => o.value === value);
  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={triggerClassName || 'inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold bg-white border border-stone-300 text-stone-700 hover:bg-stone-50'}
      >
        {label}: {current?.label || value}
        <ChevronDown size={12} />
      </button>
      {open && (
        <div className="absolute left-0 top-full mt-1 z-30 bg-white border border-stone-200 rounded-lg shadow-xl py-1 min-w-[10rem] max-h-72 overflow-y-auto">
          {options.map((o) => (
            <button
              key={o.value}
              type="button"
              onClick={() => { onChange(o.value); setOpen(false); }}
              className={`w-full text-left px-3 py-2 text-xs hover:bg-stone-50 ${o.value === value ? 'font-semibold text-stone-900' : 'text-stone-600'}`}
            >
              {o.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * The assignee control.
 *
 * IT EMITS A PATCH, NOT A STRING. An assignment has to carry BOTH the id and
 * the email -- dev and prod have separate admin pools, so the same human has a
 * different Cognito `sub` per stage and the service matches prod issues to
 * testers by email instead. Building that patch here, in the one control that
 * assigns, is what stops a second assignment path being added later that sends
 * the id alone and silently hides prod issues from whoever they were given to.
 *
 * `buildAssignmentPatch` always includes `assigneeEmail`, as `null` when it is
 * unknown -- see the note on it in issuesApi.ts for why absent is worse.
 */
export const AssigneeField: React.FC<{
  value: string; onSave: (patch: IssueAssignmentPatch) => void;
}> = ({ value, onSave }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  useEffect(() => { setDraft(value); }, [value]);
  if (!editing) {
    return (
      <button
        type="button"
        onClick={() => setEditing(true)}
        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold bg-white border border-stone-300 text-stone-700 hover:bg-stone-50"
      >
        Assignee: {value || 'Unassigned'}
      </button>
    );
  }
  return (
    <input
      autoFocus
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => { setEditing(false); if (draft !== value) onSave(buildAssignmentPatch(draft)); }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
        if (e.key === 'Escape') { setDraft(value); setEditing(false); }
      }}
      placeholder="email@rubytill.com"
      className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-white border border-red-300 text-stone-900 outline-none w-40"
    />
  );
};

const Kv: React.FC<{ label: string; value: React.ReactNode; onCopy?: () => void; copied?: boolean }> = ({ label, value, onCopy, copied }) => (
  <div className="flex items-center justify-between gap-2 text-[11.5px] py-0.5">
    <span className="text-stone-400">{label}</span>
    <span className="text-stone-800 font-semibold flex items-center gap-1 text-right truncate">
      {value}
      {onCopy && (
        <button type="button" onClick={onCopy} className="text-red-600 hover:text-red-700 shrink-0" title="Copy">
          {copied ? <Check size={11} /> : <Copy size={11} />}
        </button>
      )}
    </span>
  </div>
);

const ContactCard: React.FC<{ issue: AdminIssue }> = ({ issue }) => {
  const [copied, setCopied] = useState<string | null>(null);
  const contact = issue.contact;
  const name = contact?.name || issue.reporterName || '—';
  const email = contact?.email || issue.reporterEmail || '';
  const phone = contact?.phone;
  const prefers = contact?.preferredMethod;
  const copy = (label: string, value?: string) => {
    if (!value) return;
    navigator.clipboard.writeText(value).then(() => {
      setCopied(label);
      setTimeout(() => setCopied((c) => (c === label ? null : c)), 1500);
    }).catch(() => { /* clipboard blocked — no-op */ });
  };
  return (
    <div className="bg-stone-50 border border-stone-200 rounded-lg p-3">
      <div className="text-[10px] font-extrabold uppercase tracking-wider text-stone-400 mb-2">Contact</div>
      <Kv label="Name" value={name} />
      {email && <Kv label="Email" value={email} onCopy={() => copy('email', email)} copied={copied === 'email'} />}
      {phone && <Kv label="Phone" value={phone} onCopy={() => copy('phone', phone)} copied={copied === 'phone'} />}
      {prefers && <Kv label="Prefers" value={prefers === 'phone' ? 'Phone first' : 'Email'} />}
    </div>
  );
};

const DeviceCard: React.FC<{ issue: AdminIssue }> = ({ issue }) => {
  const d = issue.device;
  if (!d) {
    return (
      <div className="bg-stone-50 border border-stone-200 rounded-lg p-3">
        <div className="text-[10px] font-extrabold uppercase tracking-wider text-stone-400 mb-2">Device</div>
        <div className="text-[11.5px] text-stone-400">No device attached</div>
      </div>
    );
  }
  return (
    <div className="bg-stone-50 border border-stone-200 rounded-lg p-3">
      <div className="text-[10px] font-extrabold uppercase tracking-wider text-stone-400 mb-2">Device</div>
      <Kv label="Model" value={d.modelName || d.manufacturer || '—'} />
      {d.serialNumber && <Kv label="Serial" value={<span className="font-mono">{d.serialNumber}</span>} />}
      <Kv label="Location" value={[d.registerName, d.locationName].filter(Boolean).join(' · ') || '—'} />
      {d.terminalType && <Kv label="Type" value={d.terminalType} />}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Timeline — comments + status changes for a support-service issue
// ---------------------------------------------------------------------------
type TimelineEvent =
  | { kind: 'created'; ts: number }
  | { kind: 'status'; ts: number; text: string }
  | { kind: 'call'; ts: number; comment: AdminIssueComment }
  | { kind: 'bubble'; ts: number; comment: AdminIssueComment; side: 'left' | 'right' };

const buildTimeline = (issue: AdminIssue, comments: AdminIssueComment[]): TimelineEvent[] => {
  const events: TimelineEvent[] = [{ kind: 'created', ts: issue.createdAt }];
  [...comments].sort((a, b) => a.createdAt - b.createdAt).forEach((c) => {
    if (c.kind === 'status_change' || c.authorRole === 'system') {
      events.push({ kind: 'status', ts: c.createdAt, text: c.body });
    } else if (c.kind === 'phone_call') {
      events.push({ kind: 'call', ts: c.createdAt, comment: c });
    } else {
      events.push({ kind: 'bubble', ts: c.createdAt, comment: c, side: c.authorRole === 'reporter' ? 'left' : 'right' });
    }
  });
  return events.sort((a, b) => a.ts - b.ts);
};

const Timeline: React.FC<{ issue: AdminIssue; comments: AdminIssueComment[]; tenantLabel: string }> = ({ issue, comments, tenantLabel }) => {
  const events = useMemo(() => buildTimeline(issue, comments), [issue, comments]);
  return (
    <div className="space-y-3">
      {events.map((e, i) => {
        if (e.kind === 'created') {
          return (
            <div key={i} className="flex items-start gap-2 text-[11.5px] text-stone-400">
              <span className="w-6 h-6 rounded-full bg-stone-100 border border-stone-200 flex items-center justify-center text-[11px] shrink-0">+</span>
              <div>
                <span className="text-stone-600 font-semibold">Ticket created</span> — {KIND_LABEL[effectiveKind(issue)]} request
                <div className="text-[10px] text-stone-400 mt-0.5">{fmtFullDate(e.ts)}</div>
              </div>
            </div>
          );
        }
        if (e.kind === 'status') {
          return (
            <div key={i} className="flex items-start gap-2 text-[11.5px] text-stone-400">
              <span className="w-6 h-6 rounded-full bg-stone-100 border border-stone-200 flex items-center justify-center text-[11px] shrink-0">⇄</span>
              <div>
                <span className="text-stone-600">{e.text}</span>
                <div className="text-[10px] text-stone-400 mt-0.5">{fmtFullDate(e.ts)}</div>
              </div>
            </div>
          );
        }
        if (e.kind === 'call') {
          return (
            <div key={i} className="bg-amber-50 border border-dashed border-amber-400 rounded-lg px-3 py-2 text-[12px] text-amber-800">
              📞 <span className="font-bold text-amber-900">Phone call</span> — {e.comment.body}
              <div className="text-[10px] text-amber-600 mt-1">
                Logged by {e.comment.authorName || e.comment.authorEmail} · {fmtFullDate(e.ts)}
              </div>
            </div>
          );
        }
        const isRight = e.side === 'right';
        return (
          <div
            key={i}
            className={`max-w-[85%] rounded-xl px-3 py-2 text-[12.5px] leading-relaxed border ${isRight ? 'ml-auto bg-[#fdf2f4] border-red-100 text-stone-800' : 'bg-blue-50/70 border-blue-100 text-stone-800'}`}
          >
            <div className={`text-[10.5px] font-bold mb-1 ${isRight ? 'text-red-700' : 'text-blue-700'}`}>
              {e.comment.authorName || e.comment.authorEmail || (isRight ? 'Support' : tenantLabel)}
              {e.comment.internal && (
                <span className="ml-2 px-1.5 py-0.5 rounded bg-stone-200 text-stone-500 text-[9px] font-semibold normal-case align-middle">internal</span>
              )}
            </div>
            <div className="whitespace-pre-wrap">{e.comment.body}</div>
            <div className="text-[10px] opacity-60 mt-1">{fmtFullDate(e.ts)}</div>
          </div>
        );
      })}
      {events.length === 1 && <div className="text-[12px] text-stone-400">No replies yet.</div>}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Log call modal — custom, no native controls
// ---------------------------------------------------------------------------
const CALL_OUTCOMES = ['Resolved on call', 'Needs follow-up', 'No answer'] as const;

const LogCallModal: React.FC<{ onClose: () => void; onSubmit: (body: string) => Promise<boolean> }> = ({ onClose, onSubmit }) => {
  const [outcome, setOutcome] = useState<typeof CALL_OUTCOMES[number]>('Resolved on call');
  const [duration, setDuration] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    setSubmitting(true);
    const body = `${outcome} · ${duration || '0'} min — ${notes}`;
    const ok = await onSubmit(body);
    setSubmitting(false);
    if (ok) onClose();
  };

  return (
    <div className="fixed inset-0 z-[10000] bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
        <div className="p-4 border-b border-stone-200 flex items-center justify-between">
          <div className="text-base font-semibold text-stone-900 flex items-center gap-2">
            <Phone size={16} style={{ color: RUBY }} /> Log a call
          </div>
          <button type="button" onClick={onClose} className="text-stone-400 hover:text-stone-600">
            <X size={18} />
          </button>
        </div>
        <div className="p-4 space-y-3">
          <div>
            <div className="text-xs font-semibold text-stone-500 mb-1.5">Outcome</div>
            <div className="flex flex-col gap-1.5">
              {CALL_OUTCOMES.map((o) => (
                <button
                  key={o}
                  type="button"
                  onClick={() => setOutcome(o)}
                  className={`text-left px-3 py-2 rounded-lg text-sm border ${outcome === o ? 'border-red-300 bg-[#fdf2f4] text-stone-900 font-medium' : 'border-stone-200 text-stone-600 hover:bg-stone-50'}`}
                >
                  {o}
                </button>
              ))}
            </div>
          </div>
          <div>
            <div className="text-xs font-semibold text-stone-500 mb-1.5">Duration (minutes)</div>
            <input
              type="number"
              min={0}
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
              className="w-full border border-stone-300 rounded-lg px-3 py-2 text-sm text-stone-900 focus:outline-none focus:border-red-400"
              placeholder="12"
            />
          </div>
          <div>
            <div className="text-xs font-semibold text-stone-500 mb-1.5">Notes</div>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="w-full border border-stone-300 rounded-lg px-3 py-2 text-sm text-stone-900 focus:outline-none focus:border-red-400 resize-none"
              placeholder="What happened on the call?"
            />
          </div>
        </div>
        <div className="p-4 border-t border-stone-200 flex justify-end gap-2">
          <button type="button" onClick={onClose} disabled={submitting} className="px-4 py-2 text-sm rounded-lg border border-stone-300 text-stone-700 hover:bg-stone-50 disabled:opacity-50">
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={submitting || !notes.trim()}
            className="px-4 py-2 text-sm rounded-lg text-white font-semibold disabled:opacity-50 inline-flex items-center gap-1.5"
            style={{ background: RUBY }}
          >
            {submitting && <RubyGemMark size={14} drawing color="currentColor" />}
            Log call
          </button>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------
const SuperAdminSupportTickets: React.FC = () => {
  const [issues, setIssues] = useState<AdminIssue[]>([]);
  const [legacyTickets, setLegacyTickets] = useState<LegacyTicket[]>([]);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);

  const [viewMode, setViewMode] = useState<'tenant' | 'all'>('tenant');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [typeFilter, setTypeFilter] = useState('all');
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<{ issue: AdminIssue; comments: AdminIssueComment[] } | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const [replyText, setReplyText] = useState('');
  const [internalNote, setInternalNote] = useState(false);
  const [posting, setPosting] = useState(false);
  const [logCallOpen, setLogCallOpen] = useState(false);
  const [deleteLegacyCandidate, setDeleteLegacyCandidate] = useState<string | null>(null);

  // -- Load ---------------------------------------------------------------
  const loadIssues = useCallback(async (): Promise<AdminIssue[]> => {
    // No status filter unions only the 3 active statuses on the backend, so
    // resolved/rejected are fetched with their own explicit queries and
    // merged client-side. Promise.allSettled so one failing bucket doesn't
    // blank the others.
    const settled = await Promise.allSettled([
      issuesApi.list({ limit: 200 }),
      issuesApi.list({ status: 'resolved', limit: 100 }),
      issuesApi.list({ status: 'rejected', limit: 100 }),
    ]);
    const merged = new Map<string, AdminIssue>();
    let anyFailed = false;
    settled.forEach((r) => {
      if (r.status === 'fulfilled') {
        (r.value.issues || []).forEach((i) => merged.set(i.id, i));
      } else {
        anyFailed = true;
      }
    });
    if (anyFailed) toastService.error('Some tickets failed to load — showing partial results');
    return Array.from(merged.values());
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const iss = await loadIssues();
      setIssues(iss);
    } catch (e: any) {
      toastService.error(e?.message || 'Failed to load support tickets');
    }
    try {
      const tenantList = await api.tenants.getAll();
      setTenants(tenantList || []);
    } catch {
      // Soft fallback — grouping still works keyed by tenantId /
      // context.tenantName — but the operator needs to know names are stale.
      toastService.error('Tenant names unavailable — showing tenant IDs');
    }
    try {
      const data = await authedRequest<{ tickets?: LegacyTicket[] }>(`${EXT_API}/admin/support/tickets`);
      setLegacyTickets(data.tickets || []);
    } catch (e: any) {
      toastService.error(`Guest tickets failed to load: ${e?.message || 'unknown error'}`);
    }
    setLoading(false);
  }, [loadIssues]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const tenantsById = useMemo(() => new Map(tenants.map((t) => [t.id, t])), [tenants]);

  // -- Detail load for support-service issues ------------------------------
  const loadIssueDetail = useCallback(async (id: string) => {
    setDetailLoading(true);
    try {
      const res = await issuesApi.get(id);
      setDetail(res);
    } catch (e: any) {
      toastService.error(e?.message || 'Failed to load ticket');
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const legacySelected = useMemo(
    () => legacyTickets.find((t) => t.id === selectedId) || null,
    [legacyTickets, selectedId],
  );

  useEffect(() => {
    setReplyText('');
    setInternalNote(false);
    if (!selectedId) { setDetail(null); return; }
    const isLegacy = legacyTickets.some((t) => t.id === selectedId);
    if (isLegacy) { setDetail(null); return; }
    loadIssueDetail(selectedId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  // -- Unified rows / filtering / grouping ---------------------------------
  const allRows = useMemo(() => [
    ...issues.map((i) => fromIssue(i, tenantsById)),
    ...legacyTickets.map(fromLegacy),
  ], [issues, legacyTickets, tenantsById]);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return allRows.filter((r) => {
      if (typeFilter !== 'all') {
        if (typeFilter === 'legacy' ? !r.legacy : r.kind !== typeFilter) return false;
      }
      if (statusFilter === 'active') {
        if (isTerminalStatus(r.status)) return false;
      } else if (statusFilter !== 'all' && r.status !== statusFilter) {
        return false;
      }
      if (q) {
        const hay = `${r.title} ${r.tenantName} ${r.serial || ''} ${r.id}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [allRows, typeFilter, statusFilter, search]);

  const tenantGroups = useMemo(() => {
    const map = new Map<string, { key: string; tenantName: string; legacy: boolean; rows: UnifiedRow[] }>();
    filteredRows.forEach((r) => {
      const g = map.get(r.tenantKey) || { key: r.tenantKey, tenantName: r.tenantName, legacy: r.legacy || r.tenantKey === 'legacy', rows: [] as UnifiedRow[] };
      g.rows.push(r);
      map.set(r.tenantKey, g);
    });
    const groups = Array.from(map.values());
    groups.forEach((g) => g.rows.sort((a, b) => b.createdAt - a.createdAt));
    const nonLegacy = groups.filter((g) => !g.legacy).sort((a, b) => {
      const am = Math.max(...a.rows.map((r) => r.createdAt));
      const bm = Math.max(...b.rows.map((r) => r.createdAt));
      return bm - am;
    });
    const legacyGroup = groups.find((g) => g.legacy);
    return legacyGroup ? [...nonLegacy, legacyGroup] : nonLegacy;
  }, [filteredRows]);

  const flatRows = useMemo(() => [...filteredRows].sort((a, b) => b.createdAt - a.createdAt), [filteredRows]);

  // -- Header stats ---------------------------------------------------------
  const statOpen = useMemo(() => allRows.filter((r) => !isTerminalStatus(r.status) && !isWaitingStatus(r.status)).length, [allRows]);
  const statWaiting = useMemo(() => allRows.filter((r) => isWaitingStatus(r.status)).length, [allRows]);
  const statUrgent = useMemo(() => allRows.filter((r) => isUrgentPriority(r.priority) && !isTerminalStatus(r.status)).length, [allRows]);

  // -- Mutations: support-service issue ------------------------------------
  const updateIssueField = async (
    id: string,
    // `assigneeEmail` is `string | null` and NEVER optional in an assignment
    // patch -- `buildAssignmentPatch` guarantees the key is there.
    patch: { status?: string; priority?: string } | IssueAssignmentPatch,
  ) => {
    const prevIssue = detail?.issue;
    setDetail((d) => (d && d.issue.id === id ? { ...d, issue: { ...d.issue, ...patch } as AdminIssue } : d));
    setIssues((list) => list.map((i) => (i.id === id ? ({ ...i, ...patch } as AdminIssue) : i)));
    try {
      const res = await issuesApi.update(id, patch as any);
      setDetail((d) => (d && d.issue.id === id ? { ...d, issue: res.issue } : d));
      setIssues((list) => list.map((i) => (i.id === id ? res.issue : i)));
    } catch (e: any) {
      if (prevIssue) setDetail((d) => (d && d.issue.id === id ? { ...d, issue: prevIssue } : d));
      toastService.error(e?.message || 'Failed to update ticket');
    }
  };

  const postComment = async (id: string, body: string, opts: { internal?: boolean; kind?: 'comment' | 'phone_call' } = {}): Promise<boolean> => {
    if (!body.trim() || !detail) return false;
    setPosting(true);
    const optimistic: AdminIssueComment = {
      id: `optimistic-${Date.now()}`,
      issueId: id,
      authorId: 'me',
      authorEmail: '',
      authorName: 'You',
      authorRole: 'super_admin',
      body,
      internal: !!opts.internal,
      kind: opts.kind || 'comment',
      createdAt: Date.now(),
    };
    setDetail((d) => (d ? { ...d, comments: [...d.comments, optimistic] } : d));
    try {
      await issuesApi.comment(id, body, opts);
      await loadIssueDetail(id);
      return true;
    } catch (e: any) {
      setDetail((d) => (d ? { ...d, comments: d.comments.filter((c) => c.id !== optimistic.id) } : d));
      toastService.error(e?.message || 'Failed to post');
      return false;
    } finally {
      setPosting(false);
    }
  };

  const submitReply = async () => {
    if (!detail || !replyText.trim()) return;
    const ok = await postComment(detail.issue.id, replyText.trim(), { internal: internalNote, kind: 'comment' });
    if (ok) { setReplyText(''); setInternalNote(false); }
  };

  // -- Mutations: legacy ext ticket -----------------------------------------
  const updateLegacyStatus = async (id: string, status: string) => {
    const prev = legacyTickets;
    setLegacyTickets((ts) => ts.map((t) => (t.id === id ? { ...t, status } : t)));
    try {
      await authedRequest(`${EXT_API}/admin/support/tickets/${id}`, { method: 'PUT', body: JSON.stringify({ status }) });
    } catch (e: any) {
      setLegacyTickets(prev);
      toastService.error(e?.message || 'Failed to update status');
    }
  };

  const sendLegacyReply = async () => {
    if (!legacySelected || !replyText.trim()) return;
    setPosting(true);
    try {
      await authedRequest(`${EXT_API}/admin/support/tickets/${legacySelected.id}/reply`, {
        method: 'POST', body: JSON.stringify({ message: replyText.trim() }),
      });
      const data = await authedRequest<{ ticket: LegacyTicket }>(`${EXT_API}/admin/support/tickets/${legacySelected.id}`);
      setLegacyTickets((ts) => ts.map((t) => (t.id === legacySelected.id ? data.ticket : t)));
      setReplyText('');
      toastService.success('Reply sent');
    } catch (e: any) {
      toastService.error(e?.message || 'Failed to send reply');
    } finally {
      setPosting(false);
    }
  };

  const deleteLegacyTicket = async (id: string) => {
    const prev = legacyTickets;
    setLegacyTickets((ts) => ts.filter((t) => t.id !== id));
    try {
      await authedRequest(`${EXT_API}/admin/support/tickets/${id}`, { method: 'DELETE' });
      if (selectedId === id) setSelectedId(null);
      toastService.success('Ticket deleted');
    } catch (e: any) {
      setLegacyTickets(prev);
      toastService.error(e?.message || 'Failed to delete ticket');
    } finally {
      setDeleteLegacyCandidate(null);
    }
  };

  // -- Row / group selection helpers ----------------------------------------
  const selectRow = (row: UnifiedRow) => {
    setSelectedId(row.id);
    setExpandedGroups((prev) => new Set(prev).add(row.tenantKey));
  };
  const toggleGroup = (key: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const selectedTenantName = detail
    ? (tenantsById.get(detail.issue.tenantId)?.name || detail.issue.context?.tenantName || 'Unknown tenant')
    : legacySelected
      ? (legacySelected.businessName || legacySelected.name)
      : '';

  return (
    <div className="min-h-screen bg-black -m-3 md:-m-6">
      {/* Header — black banner, ruby icon, live counters */}
      <div className="bg-black">
        <div className="px-4 md:px-6 py-3 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3 flex-shrink-0">
            <div className="w-10 h-10 rounded-[10px] flex items-center justify-center" style={{ background: RUBY }}>
              <Headphones size={20} className="text-white" />
            </div>
            <div>
              <h1 className="text-base md:text-lg font-semibold text-white">Support Center</h1>
              <p className="text-[11px] text-gray-400">Every tenant request, call &amp; conversation in one queue</p>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <span className="bg-[#17171a] border border-[#2c2c30] rounded-lg px-3 py-1.5 text-xs text-stone-300">
              <b className="text-white text-sm mr-1">{statOpen}</b>Open
            </span>
            <span className="bg-[#17171a] border border-[#2c2c30] rounded-lg px-3 py-1.5 text-xs text-stone-300">
              <b className="text-white text-sm mr-1">{statWaiting}</b>Waiting
            </span>
            <span className="bg-[#17171a] border border-[#2c2c30] rounded-lg px-3 py-1.5 text-xs text-stone-300">
              <b className="mr-1 text-sm" style={{ color: statUrgent > 0 ? '#ff5f78' : '#fff' }}>{statUrgent}</b>Urgent
            </span>
            <button
              onClick={loadAll}
              disabled={loading}
              className="p-2 rounded-lg hover:bg-white/10 transition-colors text-gray-400 hover:text-white"
              title="Refresh"
            >
              {loading ? <RubyGemMark size={16} drawing color="currentColor" /> : <RefreshCw className="w-4 h-4" />}
            </button>
          </div>
        </div>
        <div style={{ background: `linear-gradient(90deg, #e8365a, ${RUBY}, #9F1239)`, height: '3px' }} />
      </div>

      <div className="bg-stone-100 min-h-[calc(100vh-72px)]">
        {/* Toolbar */}
        <div className="p-4 flex flex-wrap items-center gap-2.5">
          <div className="flex bg-white border border-stone-200 rounded-lg p-1">
            <button
              type="button"
              onClick={() => setViewMode('tenant')}
              className={`px-3.5 py-1.5 rounded-md text-xs font-semibold ${viewMode === 'tenant' ? 'text-white' : 'text-stone-500 hover:text-stone-700'}`}
              style={viewMode === 'tenant' ? { background: RUBY } : undefined}
            >
              By Tenant
            </button>
            <button
              type="button"
              onClick={() => setViewMode('all')}
              className={`px-3.5 py-1.5 rounded-md text-xs font-semibold ${viewMode === 'all' ? 'text-white' : 'text-stone-500 hover:text-stone-700'}`}
              style={viewMode === 'all' ? { background: RUBY } : undefined}
            >
              All Tickets
            </button>
          </div>

          <div className="relative flex-1 min-w-[180px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-stone-400" />
            <input
              type="text"
              placeholder="Search tickets, tenants, serial numbers…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-2 bg-white border border-stone-200 rounded-lg text-xs text-stone-800 placeholder-stone-400 focus:outline-none focus:border-red-400"
            />
          </div>

          <Dropdown label="Status" value={statusFilter} options={STATUS_FILTER_OPTIONS} onChange={setStatusFilter} />
          <Dropdown label="Type" value={typeFilter} options={TYPE_FILTER_OPTIONS} onChange={setTypeFilter} />
        </div>

        {/* Body */}
        <div className="grid grid-cols-1 lg:grid-cols-[1.05fr_1fr] gap-4 px-4 pb-6 items-start">
          {/* Left: groups or flat list */}
          <div className={`${selectedId ? 'hidden lg:block' : ''} space-y-2.5`}>
            {loading && allRows.length === 0 ? (
              <RubyLoader variant="section" label="Loading tickets…" className="bg-white border border-stone-200 rounded-xl" />
            ) : viewMode === 'tenant' ? (
              tenantGroups.length === 0 ? (
                <EmptyRows />
              ) : tenantGroups.map((g) => {
                const urgent = g.rows.filter((r) => isUrgentPriority(r.priority) && !isTerminalStatus(r.status)).length;
                const inProgress = g.rows.filter((r) => r.status === 'in_progress').length;
                const openCt = g.rows.filter((r) => !isTerminalStatus(r.status) && !isWaitingStatus(r.status)).length;
                const isOpen = expandedGroups.has(g.key);
                return (
                  <div key={g.key} className="bg-white border border-stone-200 rounded-xl overflow-hidden">
                    <div onClick={() => toggleGroup(g.key)} className="flex items-center gap-2.5 px-4 py-3 cursor-pointer">
                      <span className="w-8 h-8 rounded-lg bg-[#fde8ec] text-red-600 font-extrabold text-[11px] flex items-center justify-center flex-none">
                        {g.legacy ? '—' : initials(g.tenantName)}
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="text-[13px] font-bold text-stone-900 truncate">{g.tenantName}</div>
                        <div className="text-[10.5px] text-stone-400 truncate">
                          {g.legacy ? 'Contact-form tickets with no tenant account' : `${g.rows.length} ticket${g.rows.length === 1 ? '' : 's'}`}
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5 flex-none">
                        {g.legacy && <span className="text-[9px] font-extrabold bg-stone-100 text-stone-500 rounded px-1.5 py-0.5 uppercase">Legacy</span>}
                        {urgent > 0 && <span className="text-[10px] font-bold rounded-full px-2 py-0.5 bg-[#fde8ec] text-red-600">{urgent} urgent</span>}
                        {inProgress > 0 && <span className="text-[10px] font-bold rounded-full px-2 py-0.5 bg-amber-100 text-amber-700">{inProgress} in progress</span>}
                        {urgent === 0 && inProgress === 0 && openCt > 0 && <span className="text-[10px] font-bold rounded-full px-2 py-0.5 bg-blue-50 text-blue-600">{openCt} open</span>}
                        <ChevronDown size={14} className={`text-stone-400 transition-transform ${isOpen ? '' : '-rotate-90'}`} />
                      </div>
                    </div>
                    {isOpen && (
                      <div className="border-t border-stone-100">
                        {g.rows.map((r) => (
                          <TicketRow key={r.id} row={r} selected={selectedId === r.id} onClick={() => selectRow(r)} />
                        ))}
                      </div>
                    )}
                  </div>
                );
              })
            ) : (
              <div className="bg-white border border-stone-200 rounded-xl overflow-hidden">
                {flatRows.length === 0 ? <EmptyRows /> : flatRows.map((r) => (
                  <TicketRow key={r.id} row={r} selected={selectedId === r.id} onClick={() => selectRow(r)} showTenant />
                ))}
              </div>
            )}
          </div>

          {/* Right: detail */}
          <div className={`${selectedId ? '' : 'hidden lg:block'} lg:sticky lg:top-4`}>
            {!selectedId ? (
              <div className="bg-white border border-stone-200 rounded-xl p-10 text-center text-stone-400">
                Select a ticket to view details and reply.
              </div>
            ) : legacySelected ? (
              <LegacyDetailPanel
                ticket={legacySelected}
                replyText={replyText}
                setReplyText={setReplyText}
                posting={posting}
                onBack={() => setSelectedId(null)}
                onChangeStatus={(s) => updateLegacyStatus(legacySelected.id, s)}
                onSendReply={sendLegacyReply}
                onDelete={() => setDeleteLegacyCandidate(legacySelected.id)}
              />
            ) : detailLoading && !detail ? (
              <RubyLoader variant="section" label="Loading ticket…" className="bg-white border border-stone-200 rounded-xl" />
            ) : detail ? (
              <div className="bg-white border border-stone-200 rounded-xl overflow-hidden flex flex-col">
                <div className="p-4 border-b border-stone-200">
                  <button type="button" onClick={() => setSelectedId(null)} className="lg:hidden text-xs text-stone-400 mb-2">← Back</button>
                  <div className="flex items-center gap-2 flex-wrap">
                    <h2 className="flex-1 text-[15px] font-bold text-stone-900">{detail.issue.title}</h2>
                    <StatusPill status={detail.issue.status} />
                  </div>
                  <div className="text-[11px] text-stone-400 mt-1">
                    <span className="font-mono">#{detail.issue.id.slice(-8).toUpperCase()}</span> · {KIND_LABEL[effectiveKind(detail.issue)]} · {selectedTenantName} · Opened {fmtFullDate(detail.issue.createdAt)}
                  </div>
                </div>

                <div className="p-3 border-b border-stone-200 flex flex-wrap gap-2 items-center">
                  <Dropdown label="Status" value={detail.issue.status} options={STATUS_OPTIONS} onChange={(v) => updateIssueField(detail.issue.id, { status: v })} />
                  <Dropdown label="Priority" value={detail.issue.priority} options={PRIORITY_OPTIONS} onChange={(v) => updateIssueField(detail.issue.id, { priority: v })} />
                  <AssigneeField
                    value={detail.issue.assigneeEmail || detail.issue.assigneeId || ''}
                    onSave={(patch) => updateIssueField(detail.issue.id, patch)}
                  />
                  <button
                    type="button"
                    onClick={() => setLogCallOpen(true)}
                    className="ml-auto inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-white text-xs font-bold"
                    style={{ background: RUBY }}
                  >
                    <Phone size={13} /> Log call
                  </button>
                </div>

                <div className="p-3 border-b border-stone-200 grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                  <ContactCard issue={detail.issue} />
                  <DeviceCard issue={detail.issue} />
                </div>

                <div className="p-4 max-h-[360px] overflow-y-auto">
                  <Timeline issue={detail.issue} comments={detail.comments} tenantLabel={selectedTenantName} />
                </div>

                <div className="p-3 border-t border-stone-200">
                  <textarea
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter' && e.metaKey) submitReply(); }}
                    rows={2}
                    placeholder={`Reply to ${selectedTenantName}… (Cmd+Enter to send)`}
                    className="w-full bg-stone-50 border border-stone-200 rounded-lg px-3 py-2 text-[13px] text-stone-800 placeholder-stone-400 focus:outline-none focus:border-red-400 resize-none"
                  />
                  <div className="flex items-center justify-between mt-2">
                    <label className="flex items-center gap-1.5 text-[11px] text-stone-500 cursor-pointer">
                      <input type="checkbox" checked={internalNote} onChange={(e) => setInternalNote(e.target.checked)} />
                      Internal note
                    </label>
                    <button
                      type="button"
                      onClick={submitReply}
                      disabled={posting || !replyText.trim()}
                      className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-white text-xs font-bold disabled:opacity-50"
                      style={{ background: RUBY }}
                    >
                      {posting ? <RubyGemMark size={13} drawing color="currentColor" /> : <Send size={13} />}
                      Send
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-sm text-red-700 flex items-center gap-2">
                <AlertCircle size={16} /> Ticket not found or failed to load.
              </div>
            )}
          </div>
        </div>
      </div>

      {logCallOpen && detail && (
        <LogCallModal
          onClose={() => setLogCallOpen(false)}
          onSubmit={(body) => postComment(detail.issue.id, body, { kind: 'phone_call', internal: false })}
        />
      )}

      {deleteLegacyCandidate && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white border border-stone-200 rounded-2xl p-6 max-w-sm w-full shadow-2xl">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-11 h-11 rounded-xl bg-red-100 flex items-center justify-center">
                <Trash2 className="w-5 h-5 text-red-500" />
              </div>
              <div>
                <h3 className="font-semibold text-stone-800 text-base">Delete ticket?</h3>
                <p className="text-xs text-stone-500">This cannot be undone</p>
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={() => setDeleteLegacyCandidate(null)} className="flex-1 px-4 py-2.5 bg-stone-100 hover:bg-stone-200 text-stone-700 rounded-xl text-sm font-medium">
                Cancel
              </button>
              <button onClick={() => deleteLegacyTicket(deleteLegacyCandidate)} className="flex-1 px-4 py-2.5 bg-red-500 hover:bg-red-600 text-white rounded-xl text-sm font-medium">
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const EmptyRows: React.FC = () => (
  <div className="bg-white border border-stone-200 rounded-xl p-10 text-center text-stone-400 flex flex-col items-center gap-2">
    <AlertCircle size={24} />
    No tickets match these filters.
  </div>
);

const TicketRow: React.FC<{ row: UnifiedRow; selected: boolean; onClick: () => void; showTenant?: boolean }> = ({ row, selected, onClick, showTenant }) => (
  <div
    onClick={onClick}
    className={`flex items-center gap-2.5 px-4 py-2.5 border-b border-stone-100 last:border-b-0 cursor-pointer text-sm ${selected ? 'bg-[#fdf2f4] shadow-[inset_3px_0_0_#DC143C]' : 'hover:bg-stone-50'}`}
  >
    <span className="text-[14px] flex-none">{KIND_EMOJI[row.kind] || '✉️'}</span>
    <div className="flex-1 min-w-0">
      <div className="text-stone-900 text-[13px] font-medium truncate">{row.title}</div>
      <div className="text-[10.5px] text-stone-400 truncate flex items-center gap-1.5">
        {KIND_LABEL[row.kind] || 'Ticket'}
        {showTenant && !row.legacy && <span className="truncate">· {row.tenantName}</span>}
        {row.legacy && <span className="text-[9px] font-extrabold bg-stone-100 text-stone-500 rounded px-1 py-0.5 uppercase">Legacy</span>}
      </div>
    </div>
    <StatusPill status={row.status} />
    <span className="text-[10.5px] text-stone-400 flex-none">{timeAgo(row.createdAt)}</span>
  </div>
);

const LegacyDetailPanel: React.FC<{
  ticket: LegacyTicket;
  replyText: string;
  setReplyText: (v: string) => void;
  posting: boolean;
  onBack: () => void;
  onChangeStatus: (s: string) => void;
  onSendReply: () => void;
  onDelete: () => void;
}> = ({ ticket, replyText, setReplyText, posting, onBack, onChangeStatus, onSendReply, onDelete }) => (
  <div className="bg-white border border-stone-200 rounded-xl overflow-hidden flex flex-col">
    <div className="p-4 border-b border-stone-200">
      <button type="button" onClick={onBack} className="lg:hidden text-xs text-stone-400 mb-2">← Back</button>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[9px] font-extrabold bg-stone-100 text-stone-500 rounded px-1.5 py-0.5 uppercase">Legacy</span>
        <h2 className="flex-1 text-[15px] font-bold text-stone-900">{ticket.subject}</h2>
        <StatusPill status={ticket.status} />
      </div>
      <div className="text-[11px] text-stone-400 mt-1">
        <span className="font-mono">#{ticket.id.slice(-8).toUpperCase()}</span> · Guest inquiry · Opened {fmtFullDate(ticket.createdAt)}
      </div>
    </div>

    <div className="p-3 border-b border-stone-200 flex flex-wrap gap-2 items-center">
      <Dropdown label="Status" value={ticket.status} options={LEGACY_STATUS_OPTIONS} onChange={onChangeStatus} />
      <button type="button" onClick={onDelete} className="ml-auto p-2 text-stone-400 hover:text-red-600 hover:bg-red-50 rounded-lg" title="Delete">
        <Trash2 size={15} />
      </button>
    </div>

    <div className="p-3 border-b border-stone-200">
      <div className="bg-stone-50 border border-stone-200 rounded-lg p-3">
        <div className="text-[10px] font-extrabold uppercase tracking-wider text-stone-400 mb-2">Contact</div>
        <Kv label="Name" value={ticket.name} />
        <Kv label="Email" value={ticket.email} />
        {ticket.phone && <Kv label="Phone" value={ticket.phone} />}
        {ticket.businessName && <Kv label="Business" value={ticket.businessName} />}
      </div>
    </div>

    <div className="p-4 max-h-[360px] overflow-y-auto space-y-3">
      <div className="bg-blue-50/70 border border-blue-100 rounded-xl px-3 py-2 text-[12.5px] text-stone-800 max-w-[85%]">
        <div className="text-[10.5px] font-bold text-blue-700 mb-1">{ticket.name}</div>
        <div className="whitespace-pre-wrap">{ticket.message}</div>
        <div className="text-[10px] opacity-60 mt-1">{fmtFullDate(ticket.createdAt)}</div>
      </div>
      {(ticket.replies || []).map((r) => (
        <div
          key={r.id}
          className={`rounded-xl px-3 py-2 text-[12.5px] leading-relaxed border max-w-[85%] ${r.from === 'admin' ? 'ml-auto bg-[#fdf2f4] border-red-100 text-stone-800' : 'bg-blue-50/70 border-blue-100 text-stone-800'}`}
        >
          <div className={`text-[10.5px] font-bold mb-1 ${r.from === 'admin' ? 'text-red-700' : 'text-blue-700'}`}>
            {r.from === 'admin' ? 'Support' : ticket.name}
          </div>
          <div className="whitespace-pre-wrap">{r.message}</div>
          <div className="text-[10px] opacity-60 mt-1">{fmtFullDate(r.createdAt)}</div>
        </div>
      ))}
    </div>

    <div className="p-3 border-t border-stone-200">
      <textarea
        value={replyText}
        onChange={(e) => setReplyText(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter' && e.metaKey) onSendReply(); }}
        rows={2}
        placeholder={`Reply to ${ticket.name}… (Cmd+Enter to send)`}
        className="w-full bg-stone-50 border border-stone-200 rounded-lg px-3 py-2 text-[13px] text-stone-800 placeholder-stone-400 focus:outline-none focus:border-red-400 resize-none"
      />
      <div className="flex items-center justify-end mt-2">
        <button
          type="button"
          onClick={onSendReply}
          disabled={posting || !replyText.trim()}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-white text-xs font-bold disabled:opacity-50"
          style={{ background: RUBY }}
        >
          {posting ? <RubyGemMark size={13} drawing color="currentColor" /> : <Send size={13} />}
          Send
        </button>
      </div>
    </div>
  </div>
);

export default SuperAdminSupportTickets;
